import React, { useEffect, useState, Fragment } from 'react'
import axios from 'axios'
import { useStateValue } from '../../Utils/StateProvider'
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Colors,
    Title,
    Tooltip,
    Legend,
} from 'chart.js'
import { Line } from 'react-chartjs-2'

import { BiSend, BiTransferAlt } from 'react-icons/bi'
import { TfiLayoutListThumb } from 'react-icons/tfi'
import { TiArrowMaximise, TiFlowMerge, TiFlowParallel, TiFlowSwitch } from 'react-icons/ti'
import { CgMoreVertical } from 'react-icons/cg'
import { AiOutlineNumber, AiOutlineUser, AiOutlineUserAdd } from 'react-icons/ai'
import { IoCopyOutline } from 'react-icons/io5'
import { RiNumber0, 
    RiNumber1, 
    RiNumber2, 
    RiNumber3, 
    RiNumber4, 
    RiNumber5, 
    RiNumber6, 
    RiNumber7, 
    RiNumber8, 
    RiNumber9 } from 'react-icons/ri'

import { formatToCurrency, copyContent, capitalizeWords } from '../../Utils/general'

import processingGIF from '../../Contents/images/utils/progress.gif'
import { useCookies } from 'react-cookie'
import { BsArrowDown, BsArrowUp, BsBank } from 'react-icons/bs'
import { GrClose } from 'react-icons/gr'

import '../../Styles/Form.css'
import { ImSortAmountDesc } from 'react-icons/im'
import { SiCodenewbie } from 'react-icons/si'
import { MdClear, MdOutlineClear, MdOutlineKeyboardBackspace } from 'react-icons/md'
import { GiKeyLock } from 'react-icons/gi'
import { FiSend } from 'react-icons/fi'
import { TbArrowBackUp } from 'react-icons/tb'
import { VscSymbolNumeric } from 'react-icons/vsc'
import Payments from './Payments'
import Cards from './Cards'
import Services from './Services'

import smile from '../../Contents/images/utils/smile.jpg'
import card from '../../Contents/images/utils/card.png'
import support from '../../Contents/images/utils/support.png'


ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Colors,
    Title,
    Tooltip,
    Legend
);

const options = {
    scales: {
        x: {
            grid: {
                display: false
            }
        },
        y: {
            beginAtZero: true,
            grid: {
                display: false
            }
        }
    },
    plugins: {
        legend: {
            display: false
        }
    },
    layout: {
        padding: 10
    }
}


const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec'
]

const labels = () => {
    let month = new Date().getMonth()
    if (month >= 5) return months.slice(month - 5, month)
    else {
        let dif = 5 - month

        return [...months.slice(12 - dif, 12), ...months.slice(0, month + 1)]
    }

}

const DrawChart = ({ chartData, options }) => {
    return <Line data={chartData} options={options} />
}


const UserDashboard = ({ bearerToken }) => {
    const [{ site, url, customer, processingDash, showBalance }, payload] = useStateValue()
    const [transactions, setTransactions] = useState([])
    const [approvedPayee, setApprovedPayee] = useState([])
    const [txs, setTxs] = useState([])
    const [sentTxs, setSentTxs] = useState([])
    const [receivedTxs, setReceivedTxs] = useState([])
    const [activeSlide, setActiveSlide] = useState(0)
    const [activeGraph, setActiveGraph] = useState('income')
    const [cookies] = useCookies(['bearerToken'])
    const [showAddBenForm, setShowAddBenForm] = useState(false)
    const [activeFormPart, setActiveFormPart] = useState(1)
    const [addApprovedPayeeData, setAddApprovedPayeeData] = useState({ uid: customer?.uid, institution: site.name, domicile: 'intra', allowed_limit: '∞' })
    const [payeeLimit, setPayeeLimit] = useState('unlimited')
    const [input, setInput] = useState('')
    const [error, setError] = useState(null)
    const [chartData, setChartData] = useState({
        labels: labels(),
        datasets: [
            {
                label: '',
                data: [0, 0, 0, 0, 0, 0],
                borderColor: '#169bd7'
            }
        ]
    })

    let buttons = [
        <RiNumber0 data-number={0} onClick={e => setInput(`${input}0`)} />,
        <RiNumber1 data-number={1} onClick={e => setInput(`${input}1`)} />,
        <RiNumber2 data-number={2} onClick={e => setInput(`${input}2`)} />,
        <RiNumber3 data-number={3} onClick={e => setInput(`${input}3`)} />,
        <RiNumber4 data-number={4} onClick={e => setInput(`${input}4`)} />,
        <RiNumber5 data-number={5} onClick={e => setInput(`${input}5`)} />,
        <RiNumber6 data-number={6} onClick={e => setInput(`${input}6`)} />,
        <RiNumber7 data-number={7} onClick={e => setInput(`${input}7`)} />,
        <RiNumber8 data-number={8} onClick={e => setInput(`${input}8`)} />,
        <RiNumber9 data-number={9} onClick={e => setInput(`${input}9`)} />

    ]

    const clearForm = () => {
        setError(null)
        setAddApprovedPayeeData({ uid: customer?.uid, institution: site.name, domicile: 'intra', allowed_limit: '∞' })
        setActiveFormPart(1)
    }

    const sortedData = (arr, labels) => {
        let data = labels.map(label => {
            try {
                return arr.filter(ar => months[new Date(ar.timestamp).getMonth()] === label).map(item => item.amount).reduce((a, b) => { return Math.max(a, b) })
            } catch (err) {
                return 0
            }
        })

        return data
    }

    const copyText = async text => {
        await copyContent(text)
        payload({
            type: 'SET_FLASH_NOTE',
            data: {
                note: 'copied',
                status: 'success'
            }
        })
    }

    const setUpData = async (source = null) => {
        try {
            let options 
            if (!source) options = {
                headers: {
                    Authorization: `Bearer ${bearerToken}`
                }
            }
            else options = {
                cancelToken: source.token,
                headers: {
                    Authorization: `Bearer ${bearerToken}`
                }
            }
            const __data = await axios.all([
                axios.get(`${url}/api/u?uid=${cookies?.uid}`, options),
                axios.get(`${url}/api/tx?uid=${cookies?.uid}`, options),
                axios.get(`${url}/api/u/payee?uid=${cookies?.uid}`, options)
            ])
            const [__customer, __transactions, __approvedPayee] = __data

            setTransactions(__transactions.data.transactions)
            setApprovedPayee(__approvedPayee.data.approvedPayee)

            payload({
                type: 'SET_CUSTOMER',
                data: __customer.data.data
            })

        } catch (err) {
            // console.error(err.message)
        }

    }

    const goForward = async () => {
        setError(null)
        switch(activeFormPart) {
            case 1:
                if (
                    !addApprovedPayeeData.account_name ||
                    !addApprovedPayeeData.account_number ||
                    !addApprovedPayeeData.domicile ||
                    !addApprovedPayeeData.allowed_limit ||
                    !addApprovedPayeeData.institution
                ) return setError('missing required detail(s)')

                if (
                    addApprovedPayeeData.domicile === 'international' &&
                    (
                        !addApprovedPayeeData.swift ||
                        !addApprovedPayeeData.routing    
                    )
                ) return setError('Swift Code & Routing Numbers are required for international transactions.')

                if (
                    addApprovedPayeeData.allowed_limit !== '∞' &&
                    parseFloat(addApprovedPayeeData.allowed_limit) <= 0
                ) return setError('Allowed transfer limit is required or set to infinity.')

                setActiveFormPart(2)
                break;
            case 2:
                if (!input) return setError('Provide your account secure PIN to proceed.')
                if (input !== customer?.secure_pin) return setError('Invalid PIN provided.')
                
                payload({
                    type: 'TOGGLE_PROCESSING_DASH',
                    state: true
                })

                try {
                    if (addApprovedPayeeData.allowed_limit === '∞') addApprovedPayeeData.allowed_limit = 2147483647
                    await axios.post(`${url}/api/u/payee`, addApprovedPayeeData, {
                        headers: {
                            Authorization: `Bearer ${cookies.bearerToken}`
                        }
                    })
                    await setUpData()
                    payload({
                        type: 'SET_NOTIFY',
                        data: {
                            text: 'Beneficiary successfully added.',
                            status: 'success'
                        }
                    })
                    setAddApprovedPayeeData({ uid: customer?.uid, institution: site.name, domicile: 'intra', allowed_limit: '∞' })
                    setInput('')
                    setActiveFormPart(1)
                    setShowAddBenForm(false)
                } catch(err) {
                    return setError(err.response.data.message)
                } finally {
                    payload({
                        type: 'TOGGLE_PROCESSING_DASH',
                        state: false
                    })
                }

                break;

            default:
                console.log()
        }
    }

    useEffect(() => {
        const source = axios.CancelToken.source()
        setUpData(source)
        let counter = 1

        const interval = setInterval(() => {
            setActiveSlide(counter%3)
            counter++
        }, 1000*10)


        return () => {
            source.cancel()
            clearInterval(interval)
        }
    }, [])

    useEffect(() => {
        const _sentTxs = transactions.filter(transaction => transaction.transaction_type === 'debit')
        const _receivedTxs = transactions.filter(transaction => transaction.transaction_type === 'credit')
        setSentTxs(_sentTxs)
        setReceivedTxs(_receivedTxs)
        setChartData(
            {
                labels: labels(),
                datasets: [
                    {
                        label: '',
                        data: sortedData(activeGraph === 'income' ? _receivedTxs : _sentTxs, labels()),
                        borderColor: '#169bd7'
                    }
                ]
            }
        )
    }, [transactions])

    useEffect(() => {
        setChartData(
            {
                labels: labels(),
                datasets: [
                    {
                        label: '',
                        data: sortedData(activeGraph === 'income' ? receivedTxs : sentTxs, labels()),
                        borderColor: '#169bd7'
                    }
                ]
            }
        )
    }, [activeGraph])

    useEffect(() => {
        if (!showAddBenForm) setAddApprovedPayeeData({ domicile: 'intra' })
    }, [showAddBenForm])

    useEffect(() => {
        if (payeeLimit === 'unlimited') setAddApprovedPayeeData({...addApprovedPayeeData, allowed_limit: '∞'})
        else setAddApprovedPayeeData({...addApprovedPayeeData, allowed_limit: ''})
    }, [payeeLimit])

    useEffect(() => {
        if (addApprovedPayeeData?.domicile === 'intra') setAddApprovedPayeeData({...addApprovedPayeeData, institution: site.name})
        else setAddApprovedPayeeData({...addApprovedPayeeData, institution: ''})
    }, [addApprovedPayeeData?.domicile])

    return (
        customer && (
            <Fragment>
                {
                    processingDash && (
                        <div className="content process">
                            <img src={processingGIF} alt="processing..." />
                        </div>
                    )
                }
                {
                    showAddBenForm && (
                        <div className="content add-beneficiary-form-container">
                            <div className="form-box">
                                <GrClose className='close-add-ben-form' onClick={() => setShowAddBenForm(false)} />
                                <form className="default">
                                    {
                                        error && (
                                            <div className="error-box">{error}</div>
                                        )
                                    }
                                    {
                                        activeFormPart === 1 && (
                                            <div className="form-part">
                                                <h1>Details</h1>
                                                <div className="form-row">
                                                    <div className="type-selector">
                                                        <label htmlFor="account">Transaction Type</label>
                                                        <div className="types">
                                                            <div
                                                                className={addApprovedPayeeData.domicile === "intra" ? "type active" : "type"}
                                                                onClick={() => document.getElementById('intra').click()}
                                                            >
                                                                <input
                                                                    type="radio"
                                                                    name="domicile"
                                                                    id="intra"
                                                                    value="intra"
                                                                    hidden="hidden"
                                                                    defaultChecked
                                                                    onChange={e => setAddApprovedPayeeData({ ...addApprovedPayeeData, domicile: e.target.value })}
                                                                />
                                                                {capitalizeWords(site.name)} <TiFlowParallel />
                                                            </div>
                                                            <div
                                                                className={addApprovedPayeeData.domicile === "local" ? "type active" : "type"}
                                                                onClick={() => document.getElementById('local').click()}
                                                            >
                                                                <input
                                                                    type="radio"
                                                                    name="domicile"
                                                                    id="local"
                                                                    value="local"
                                                                    hidden="hidden"
                                                                    onChange={e => setAddApprovedPayeeData({ ...addApprovedPayeeData, domicile: e.target.value })}
                                                                />
                                                                Local Banks <TiFlowMerge />
                                                            </div>
                                                            <div
                                                                className={addApprovedPayeeData.domicile === "international" ? "type active" : "type"}
                                                                onClick={() => document.getElementById('international').click()}
                                                            >
                                                                <input
                                                                    type="radio"
                                                                    name="domicile"
                                                                    id="international"
                                                                    value="international"
                                                                    hidden="hidden"
                                                                    onChange={e => setAddApprovedPayeeData({ ...addApprovedPayeeData, domicile: e.target.value })}
                                                                />
                                                                International Banks <TiFlowSwitch />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="form-row">
                                                    <div className="input-row">
                                                        <input type="text"
                                                            name="account_name"
                                                            id="account_name"
                                                            value={addApprovedPayeeData.account_name || ''}
                                                            onChange={e => setAddApprovedPayeeData({ ...addApprovedPayeeData, account_name: e.target.value })}
                                                            required
                                                        />
                                                        <label htmlFor="account_name">Account Name</label>
                                                        <AiOutlineUser />
                                                    </div>
                                                </div>
                                                <div className="form-row">
                                                    <div className="input-row">
                                                        <input type="text"
                                                            name="account_number"
                                                            id="account_number"
                                                            value={addApprovedPayeeData.account_number || ''}
                                                            onChange={e => setAddApprovedPayeeData({ ...addApprovedPayeeData, account_number: e.target.value })}
                                                            required
                                                        />
                                                        <label htmlFor="account_number">Account Number</label>
                                                        <AiOutlineNumber />
                                                    </div>
                                                </div>
                                                {
                                                    addApprovedPayeeData?.domicile && addApprovedPayeeData.domicile !== 'intra' && (
                                                        <div className="form-row">
                                                            <div className="input-row">
                                                                <input type="text"
                                                                    name="institution"
                                                                    id="institution"
                                                                    value={addApprovedPayeeData.institution || ''}
                                                                    onChange={e => setAddApprovedPayeeData({ ...addApprovedPayeeData, institution: e.target.value })}
                                                                    required
                                                                />
                                                                <label htmlFor="institution">Bank/Financial Institution</label>
                                                                <BsBank />
                                                            </div>
                                                        </div>
                                                    )
                                                }
                                                {
                                                    addApprovedPayeeData?.domicile && addApprovedPayeeData.domicile === 'international' && (
                                                        <div className="form-row split-row">
                                                            <div className="input-row">
                                                                <input type="text"
                                                                    name="swift"
                                                                    id="swift"
                                                                    value={addApprovedPayeeData.swift || ''}
                                                                    onChange={e => setAddApprovedPayeeData({ ...addApprovedPayeeData, swift: e.target.value })}
                                                                    required
                                                                />
                                                                <label htmlFor="swift">Swift Code</label>
                                                                <SiCodenewbie />
                                                            </div>
                                                            <div className="input-row">
                                                                <input type="text"
                                                                    name="routing"
                                                                    id="routing"
                                                                    value={addApprovedPayeeData.routing || ''}
                                                                    onChange={e => setAddApprovedPayeeData({ ...addApprovedPayeeData, routing: e.target.value })}
                                                                    required
                                                                />
                                                                <label htmlFor="routing">Routing Number</label>
                                                                <SiCodenewbie />
                                                            </div>
                                                        </div>
                                                    )
                                                }
                                                <div className="form-row split-row">
                                                    <div className="input-row">
                                                        <input type="text"
                                                            name="allowed_limit"
                                                            id="allowed_limit"
                                                            value={addApprovedPayeeData.allowed_limit}
                                                            onChange={e => setAddApprovedPayeeData({ ...addApprovedPayeeData, allowed_limit: e.target.value })}
                                                            required
                                                        />
                                                        <label htmlFor="allowed_limit">Allowed Transfer Limit</label>
                                                        <ImSortAmountDesc />
                                                    </div>
                                                    <div className="input-row flex-box transparent-bg">
                                                        <strong onClick={() => setPayeeLimit('unlimited')} className={payeeLimit === 'unlimited' && 'active'}>Unlimited</strong>
                                                        <button type="button" onClick={() => setPayeeLimit('custom')} className={payeeLimit === 'custom' && 'active'}>Custom Value</button>
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    }
                                    {
                                        activeFormPart === 2 && (
                                            <div className="form-part personal-data">
                                                <h1>Security</h1>
                                                <div className="form-row input-row not-required">
                                                    <input type="text" name="secure_pin" id="secure_pin" defaultValue={input} readOnly />
                                                    <label htmlFor="secure_pin">Secure PIN</label>
                                                    <GiKeyLock />
                                                </div>
                                                <div className="form-row digit-btns">
                                                    <div className="buttons">
                                                        {
                                                            buttons.map((button, index) => <div key={index} className='button'>{button}</div>)
                                                        }
                                                    </div>
                                                    <div className="controls">
                                                        <div className="control"><MdOutlineKeyboardBackspace onClick={() => setInput(input.slice(0, input.length - 1))} /></div>
                                                        <div className="control"><MdClear onClick={() => setInput('')} /></div>
                                                        <div className="control"><BiSend onClick={goForward} /></div>
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    }
                                    {
                                        activeFormPart === 1 ? (
                                            <div className="btn-row">
                                                <div className="form-btn-column">
                                                    <button type='button' onClick={goForward}>Add <FiSend /> </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="btn-row split-row">
                                                <div className="left-column">
                                                    <button type='button' onClick={() => setActiveFormPart(activeFormPart - 1)}><TbArrowBackUp /> Back</button>
                                                    <strong onClick={clearForm}>cancel <MdOutlineClear /></strong>
                                                </div>
                                                <div className="right-column">
                                                    <button type='button' onClick={goForward}>Validate <FiSend /> </button>
                                                </div>
                                            </div>
                                        )
                                    }
                                </form>
                            </div>
                        </div>
                    )
                }
                <div className='content dash'>
                    <div className="row">
                        <div className="column">
                            <div className="slides-container">
                                <div className="slides">
                                    {
                                        activeSlide === 0 && (
                                            <div className="slide">
                                                <img src={smile} alt="" />
                                            </div>
                                        )
                                    }
                                    {
                                        activeSlide === 1 && (
                                            <div className="slide">
                                                <img src={card} alt="" />
                                            </div>
                                        )
                                    }
                                    {
                                        activeSlide === 2 && (
                                            <div className="slide">
                                                <img src={support} alt="" />
                                            </div>
                                        )
                                    }
                                </div>
                                <div className="slide-control">
                                    <div className={activeSlide === 0 ? "dots active" : "dots"} onClick={() => setActiveSlide(0)}></div>
                                    <div className={activeSlide === 1 ? "dots active" : "dots"} onClick={() => setActiveSlide(1)}></div>
                                    <div className={activeSlide === 2 ? "dots active" : "dots"} onClick={() => setActiveSlide(2)}></div>
                                </div>
                            </div>
                            <ul className="shorts">
                                <li>
                                    <div className="icon-container" onClick={() => payload({type: 'SET_ACTIVE_MENU', order: 'payments,transfer'})}>
                                        <BiTransferAlt />
                                    </div>
                                    <h3>Transfer</h3>
                                </li>
                                <li>
                                    <div className="icon-container" onClick={() => payload({type: 'SET_ACTIVE_MENU', order: 'payments,history'})}>
                                        <TfiLayoutListThumb />
                                    </div>
                                    <h3>History</h3>
                                </li>
                                <li>
                                    <div className="icon-container" onClick={() => payload({ type: 'TOGGLE_SETTINGS', display: 'account'})}>
                                        <TiArrowMaximise />
                                    </div>
                                    <h3>Limits</h3>
                                </li>
                                <li>
                                    <div className="icon-container" onClick={() => payload({ type: 'SET_ACTIVE_MENU', order: 'services'})}>
                                        <CgMoreVertical />
                                    </div>
                                    <h3>More</h3>
                                </li>
                            </ul>
                            <h1>Beneficiaries</h1>
                            {
                                approvedPayee.length > 0 ? (
                                    <div className='beneficiaries'>
                                        <h5 onClick={() => setShowAddBenForm(true)}><AiOutlineUserAdd /> add new beneficiary</h5>
                                        <ul>
                                        {
                                            approvedPayee.map((ben, index) => (
                                                <li key={index} onClick={() => payload({type: 'SET_ACTIVE_MENU', order: `payments,transfer,${JSON.stringify(ben)}`})}>
                                                    <div className="abbreviation-icon">
                                                    {
                                                        ben?.account_name.split(' ').length > 1 ? `${ben?.account_name.split(' ')[0][0].toUpperCase()}${ben?.account_name.split(' ')[1][0].toUpperCase()}` : ben?.account_name.slice(0, 3).toUpperCase()
                                                    }
                                                    </div>
                                                    <h3>{ben?.account_name}</h3>
                                                    <div className="account">
                                                        <VscSymbolNumeric />
                                                        <strong>{ben?.account_number}</strong>
                                                    </div>
                                                    <div className="bank">
                                                        <BsBank />
                                                        <strong>{ben?.institution}</strong>
                                                    </div>
                                                </li>
                                            ))
                                        }
                                        </ul>
                                    </div>
                                ) : (
                                    <div className="no-list">
                                        <button onClick={() => setShowAddBenForm(true)}>
                                            Add new beneficiary <AiOutlineUserAdd />
                                        </button>
                                    </div>
                                )
                            }
                        </div>
                        <div className="column">
                            <div className="account-balance">
                                <div className="balances">
                                    <div className='main-balance'>
                                        <h3>Main Balance</h3>
                                        <h1>{showBalance ? formatToCurrency(customer?.balance, customer?.currency) : '****'}</h1>
                                    </div>
                                    <div className="ledger-balance">
                                        <h3>Ledger Balance</h3>
                                        <h1>{showBalance ? formatToCurrency(customer?.ledger_balance, customer?.currency) : '****'}</h1>
                                    </div>
                                </div>
                                <h3>Available Balance</h3>
                                <h1>{showBalance ? formatToCurrency(customer?.balance > customer?.ledger_balance ? customer?.balance + customer?.ledger_balance : customer?.balance, customer?.currency) : '****'}</h1>
                                <div className="account-data">
                                    <h1 style={{marginTop: 30}}>{customer?.fname} {customer?.mname} {customer?.lname}</h1>
                                </div>
                                <div className="account-data">
                                    <h3>Account Number:</h3>
                                    <h1>{customer.account} <IoCopyOutline onClick={() => copyText(customer?.account)} /></h1>
                                </div>
                            </div>
                            <div className="tx-chart">
                                <ul>
                                    <li className={activeGraph === 'income' ? 'active' : ''} onClick={() => setActiveGraph('income')}>Income</li>
                                    <li className={activeGraph === 'spent' ? 'active' : ''} onClick={() => setActiveGraph('spent')}>Spent</li>
                                </ul>
                                <div className="graph-container">
                                    <DrawChart chartData={chartData} options={options} />
                                </div>
                            </div>
                            <h1>Most Recent Transactions</h1>
                            <div className="recent-txs">
                                {
                                    transactions.length > 0 ? (
                                        <ul className='history'>
                                            {
                                                transactions?.map((transaction, index) => (
                                                    index < 5 && (
                                                        <li key={index} className={`history-item ${transaction?.transaction_type}`}>
                                                            <div className="abbreviation-icon">
                                                                {
                                                                    transaction?.account_name.split(' ').length > 1 ? `${transaction?.account_name.split(' ')[0][0].toUpperCase()}${transaction?.account_name.split(' ')[1][0].toUpperCase()}` : transaction?.account_name.slice(0, 3).toUpperCase()
                                                                }
                                                            </div>
                                                            <div className="data">
                                                                <h3>{capitalizeWords(transaction?.account_name)}</h3>
                                                                <p>{new Date(transaction?.timestamp).toLocaleString()}</p>
                                                            </div>
                                                            <div className="amount">
                                                                {
                                                                    transaction?.transaction_type === 'credit' ? (
                                                                        <BsArrowDown />
                                                                    ) : (
                                                                        <BsArrowUp />
                                                                    )
                                                                }
                                                                <strong>{formatToCurrency(transaction?.amount, customer?.currency)}</strong>
                                                            </div>
                                                        </li>
                                                    )
                                                ))
                                            }
                                        </ul>
                                    ) : (
                                        <div className='no-list'>No transactions yet</div>
                                    )
                                }
                            </div>
                        </div>
                    </div>
                </div>
            </Fragment>
        )
    )
}

export default UserDashboard
